/**
 * Brand portal requests and results of AI 推廣活動企劃 (campaignPlan), AI 一句話分群 (segmentNl) and
 * AI 推廣成效分析 (campaignReview). Every figure is computed by the platform; the model only writes the
 * text and picks the structures, which are checked against the segment and campaign editors' schemas.
 */
import { z } from 'zod';
import { type CampaignInput, campaignRuleSchema } from './campaigns.ts';
import type { RfmSegment, SegmentRules } from './growth.ts';
import { segmentRulesSchema } from './growth-schemas.ts';

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
const text = (max: number) => z.string().trim().min(1).max(max);

// ---------------------------------------------------------------- AI 一句話分群

export const aiSegmentRulesSchema = z.object({ text: z.string().trim().min(2).max(300) });

/** The model's reply. */
export const aiSegmentReplySchema = z.object({ name: text(80), rules: segmentRulesSchema, explanation: text(400) });

export interface AiSegmentRulesResult {
  /** Suggested segment name. */
  name: string;
  rules: SegmentRules;
  /** 「理解為」: the model's reading of the sentence, for the user to check. */
  explanation: string;
  /** What the platform took out: unknown levels, shops or sources, and conditions this brand cannot use. */
  removed: string[];
}

// ---------------------------------------------------------------- AI 推廣活動企劃 (native brands)

export const aiCampaignPlanRequestSchema = z
  .object({
    /** e.g. 「吸引三個月沒有光顧的會員回來」 */
    goal: z.string().trim().min(2).max(300),
    /** HKD the brand is prepared to give away in points and coupons. */
    budget: z.number().min(0).max(100_000_000).nullable().optional(),
    /** Hong Kong dates; without them the campaign starts today and the plan suggests its length. */
    startDate: isoDate.nullable().optional(),
    endDate: isoDate.nullable().optional(),
    audience: z.string().trim().max(200).optional(),
    notes: z.string().trim().max(500).optional(),
  })
  .refine((v) => !v.startDate || !v.endDate || v.endDate >= v.startDate, { message: 'end before start', path: ['endDate'] });
export type AiCampaignPlanRequest = z.infer<typeof aiCampaignPlanRequestSchema>;

/** The model's reply; the API also checks the ids it names and that the campaign fits campaignSchema. */
export const aiCampaignPlanReplySchema = z.object({
  summary: text(400),
  reasoning: text(1200),
  segment: z.object({ name: text(80), rules: segmentRulesSchema }),
  campaign: z.object({
    name: text(80),
    description: text(500),
    terms: text(1000),
    days: z.number().int().min(1).max(92),
    weekdays: z.array(z.number().int().min(0).max(6)).min(1).max(7).nullable(),
    timeFrom: hhmm.nullable(),
    timeTo: hhmm.nullable(),
    tierIds: z.array(z.number().int()).min(1).max(50).nullable(),
    shopIds: z.array(z.string().min(1).max(64)).min(1).max(50).nullable(),
    minAmount: z.number().min(0).max(100_000_000).nullable(),
    memberLimit: z.number().int().min(0).max(1000),
    rules: z.array(campaignRuleSchema).min(1).max(5),
  }),
  message: z.object({ title: text(60), body: text(500) }),
  measure: z.array(text(200)).min(1).max(5),
});
export type AiCampaignPlanReply = z.infer<typeof aiCampaignPlanReplySchema>;

/** What the plan was based on: the brand's last 90 days, computed by the platform. */
export interface AiCampaignContext {
  /** Hong Kong dates, inclusive. */
  from: string;
  to: string;
  members: { total: number; active30: number; active90: number; inactive90: number; newInRange: number };
  levels: Array<{ id: number; name: string; members: number }>;
  rfm: Array<{ segment: RfmSegment; members: number }>;
  sales: { count: number; amount: number; avgAmount: number; buyers: number; repeatRate: number };
  /** Sales by weekday, 0 = Sunday. */
  weekdays: Array<{ weekday: number; count: number; amount: number }>;
  shops: Array<{ id: string; name: string; sales: number }>;
  /** Active coupons that can be given (not exchange-only). */
  coupons: Array<{ id: string; name: string }>;
  /** Campaigns running or starting later. */
  campaigns: Array<{ name: string; startAt: string; endAt: string; active: boolean }>;
}

export interface AiCampaignPlan {
  summary: string;
  reasoning: string;
  /** For the 會員分群 editor. */
  segment: { name: string; rules: SegmentRules };
  /** For the 推廣活動 editor: Traditional Chinese text, switched off until the brand turns it on. */
  campaign: CampaignInput;
  /** Suggested message to the target members. */
  message: { title: string; body: string };
  /** What to watch to judge it (no promised figures). */
  measure: string[];
  basis: AiCampaignContext;
}

// ---------------------------------------------------------------- AI 推廣成效分析 (native brands)

/** Averages per member. */
export interface CampaignReviewFigures {
  spend: number;
  visits: number;
  points: number;
}

export interface CampaignReviewGroup {
  members: number;
  /** Joined during the campaign, so nothing before it. */
  joinedDuring: number;
  during: CampaignReviewFigures;
  before: CampaignReviewFigures;
  /** during − before */
  change: CampaignReviewFigures;
}

export interface CampaignReviewNumbers {
  /** The campaign so far (ISO times), and the same length of time just before it. */
  from: string;
  to: string;
  beforeFrom: string;
  days: number;
  /** Still running: `to` is now. */
  running: boolean;
  /** Members who got something from the campaign (活動記錄). */
  participants: CampaignReviewGroup;
  /** Members who joined before it and are in its levels, but got nothing from it. */
  others: CampaignReviewGroup;
  /** participants.change − others.change */
  difference: CampaignReviewFigures;
}

export const aiCampaignReviewReplySchema = z.object({ conclusion: text(600), suggestions: z.array(text(200)).min(1).max(4) });

export interface AiCampaignReviewResult {
  numbers: CampaignReviewNumbers;
  /** null: fewer than `minMembers` in a group, so the model was not asked, or `aiError`. */
  review: z.infer<typeof aiCampaignReviewReplySchema> | null;
  minMembers: number;
  /** The AI could not write the conclusion; the figures still stand. */
  aiError: string | null;
}
